import React, { useState, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApplicationSettings } from '../../contexts/ApplicationSettingsContext';
import { UserAvatar } from '../ui/UserAvatar';
import { ThemeToggle } from '../theme/ThemeToggle';
import { useRouter } from '@tanstack/react-router';
import { useQuery } from '@/apollo';
import { GET_SYSTEM_INFO } from '../../graphql/system-info';
import { DashboardHeaderContextProvider } from './DashboardHeaderContext';

interface LayoutProps {
  children: React.ReactNode;
  showHeader?: boolean;
  pageTitle?: string;
  showBackButton?: boolean;
  onBack?: () => void;
}

export const Layout: React.FC<LayoutProps> = ({ 
  children, 
  showHeader = true, 
  pageTitle,
  showBackButton = false,
  onBack
}) => {
  const { user } = useAuth();
  const { settings } = useApplicationSettings();
  const router = useRouter();
  
  // Get environment name for watermark
  const { data: systemInfoData } = useQuery(GET_SYSTEM_INFO, {
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-first',
    skip: !user // Only fetch if user is logged in
  });
  
  const environmentName = systemInfoData?.systemInfo?.application?.environmentName || '';
  const buildTime = systemInfoData?.systemInfo?.application?.buildTime;
  const gitCommit = systemInfoData?.systemInfo?.application?.gitCommit;
  const isNonProductionEnv = ['Demo', 'PreProduction', 'PreProd', 'QA', 'Development'].includes(environmentName);
  const showWatermark = isNonProductionEnv;
  const showBuildDate = (['Demo', 'PreProduction', 'PreProd', 'QA'].includes(environmentName)) && buildTime;

  // Format build date as "12/13/2025 1:10 PM PST" in user's local timezone
  const formatBuildDate = (dateString: string): string => {
    // Parse the UTC date string and convert to user's local timezone
    // JavaScript's Date automatically handles timezone conversion
    const date = new Date(dateString);
    
    // All these methods return values in the user's local timezone
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours(); // Local hours (0-23)
    const minutes = date.getMinutes(); // Local minutes (0-59)
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    // Get timezone abbreviation (e.g., PST, EST, UTC)
    const timeZoneFormatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short'
    });
    const timeZoneParts = timeZoneFormatter.formatToParts(date);
    const timeZone = timeZoneParts.find(part => part.type === 'timeZoneName')?.value || '';
    
    return `${month}/${day}/${year} ${displayHours}:${displayMinutes} ${ampm} ${timeZone}`;
  };

  const handleNavigation = (section: string) => {
    switch (section) {
      case 'dashboard':
        router.navigate({ to: '/dashboard' });
        break;
      case 'admin':
        router.navigate({ to: '/admin' });
        break;
      case 'moderation':
        router.navigate({ to: '/moderation' });
        break;
      default:
        router.navigate({ to: '/dashboard' });
        break;
    }
  };

  const [headerTabs, setHeaderTabs] = useState<React.ReactNode>(null);
  const setHeaderTabsStable = useCallback((node: React.ReactNode) => {
    setHeaderTabs(() => node);
  }, []);

  if (!showHeader || !user) {
    return <>{children}</>;
  }

  return (
    <DashboardHeaderContextProvider value={setHeaderTabsStable}>
      <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
        {/* Fixed Header: title and optional dashboard tabs in one block */}
        <div 
          className="fixed top-0 left-0 right-0 z-40 backdrop-blur-sm"
          style={{ 
            backgroundColor: 'var(--color-bg-primary)', 
            borderBottom: '1px solid var(--color-border)',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-hidden">
            {pageTitle ? (
              // Page-specific header: title row, then Back link (if any), then optional tabs
              <>
                <div className={`flex items-center justify-between relative ${showBackButton && onBack ? 'pt-4 pb-1' : headerTabs ? 'pt-4 pb-2' : 'py-4'}`}>
                  <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                    {pageTitle}
                  </h1>
                  <div className="flex items-center space-x-3">
                    <UserAvatar onNavigate={handleNavigation} />
                    <ThemeToggle />
                  </div>
                </div>
                {showBackButton && onBack && (
                  <div className="pb-2">
                    <button
                      onClick={onBack}
                      className="flex items-center gap-1.5 py-2 pr-3 pl-0 rounded-lg transition-colors text-sm -ml-1"
                      style={{
                        color: 'var(--color-text-secondary)',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--color-text-primary)';
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <ArrowLeft className="w-4 h-4 flex-shrink-0" />
                      <span>Back to Dashboard</span>
                    </button>
                  </div>
                )}
                {headerTabs && (
                  <div className="flex space-x-1 overflow-x-auto pb-3">
                    {headerTabs}
                  </div>
                )}
              </>
            ) : (
              // Default header: title row + optional dashboard tabs (same left edge)
              <>
                <div className={`flex justify-between items-center ${headerTabs ? 'pt-4 pb-2' : 'py-4'}`}>
                  <div className="flex items-center space-x-3">
                    <h1 
                      className="text-xl font-bold tracking-tight" 
                      style={{ 
                        color: 'var(--color-text-primary)',
                        letterSpacing: '-0.025em'
                      }}
                    >
                      {settings?.applicationName || 'Provider Selection Platform'}
                    </h1>
                  </div>
                  <div className="flex items-center space-x-3">
                    <UserAvatar onNavigate={handleNavigation} />
                    <ThemeToggle />
                  </div>
                </div>
                {headerTabs && (
                  <div className="flex space-x-1 overflow-x-auto pb-3">
                    {headerTabs}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      {/* Main Content - enough top padding so content clears the fixed header */}
      <div style={{
        paddingTop: (pageTitle && showBackButton && onBack && headerTabs)
          ? '150px'   // Moderator: title + Back row + tabs
          : headerTabs
            ? '120px' // Provider dashboard: title + tabs
            : (pageTitle && showBackButton && onBack)
              ? '115px' // Admin: title + Back row
              : '73px', // default: single title row
        position: 'relative'
      }}>
        {children}

        {/* Environment Watermark */}
        {showWatermark && (
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              left: '20px',
              zIndex: 9999,
              padding: '8px 16px',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: 'var(--color-text-primary)',
              opacity: 0.6,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '4px'
            }}
          >
            <div
              style={{
                fontWeight: 'bold',
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              {environmentName}
            </div>
            {showBuildDate && buildTime && (
              <>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 'normal',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    opacity: 0.8
                  }}
                >
                  Build Date: {formatBuildDate(buildTime)}
                </div>
                {gitCommit && (
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 'normal',
                      textTransform: 'none',
                      letterSpacing: '0.5px',
                      opacity: 0.8
                    }}
                  >
                    {gitCommit}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
    </DashboardHeaderContextProvider>
  );
};
